import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import useLenis from "@/hooks/useLenis";

const MAX_POLLS = 8;
const INTERVAL = 2000;

function Shell({ children }) {
  return (
    <main
      data-testid="payment-result-page"
      className="min-h-screen bg-[#090909] text-[#F2EFEB] flex flex-col items-center justify-center px-6 text-center"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        {children}
      </motion.div>
    </main>
  );
}

export function PaymentSuccess() {
  useLenis();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { removeLiveItems } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          if (!cleared.current) {
            cleared.current = true;
            removeLiveItems();
          }
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, INTERVAL);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, removeLiveItems]);

  if (state === "checking") {
    return (
      <Shell>
        <p className="font-mono-ui text-sm uppercase tracking-widest animate-pulse" data-testid="payment-checking">
          Confirming your order…
        </p>
      </Shell>
    );
  }

  if (state === "paid") {
    return (
      <Shell>
        <span className="overline text-[#DFFF00]">Order confirmed</span>
        <h1 className="font-display text-6xl md:text-7xl leading-[0.85] mt-4" data-testid="payment-success-heading">
          GRIEF<br />SECURED.
        </h1>
        <p className="font-serif-ed italic text-lg mt-6 opacity-80">
          Payment received. A receipt is on its way to your inbox — we’ll be in touch about the drop.
        </p>
        {order?.items?.length > 0 && (
          <div className="mt-8 border-2 border-[#DFFF00] text-left font-mono-ui text-sm" data-testid="payment-order-summary">
            {order.items.map((it, i) => (
              <div key={i} className="flex justify-between px-4 py-2 border-b border-[#F2EFEB]/20">
                <span className="uppercase opacity-80">{it.quantity}× {it.name}</span>
                <span>${((it.unit_amount * it.quantity) / 100).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between px-4 py-3 text-[#DFFF00] uppercase tracking-widest">
              <span>Total paid</span>
              <span data-testid="payment-total">${((order.amount || 0) / 100).toFixed(2)}</span>
            </div>
          </div>
        )}
        <Link
          to="/"
          data-testid="payment-home-btn"
          className="inline-block mt-10 font-mono-ui text-xs uppercase tracking-widest border-2 border-[#F2EFEB] px-6 py-3 hover:bg-[#DFFF00] hover:text-[#090909] hover:border-[#DFFF00] transition-colors"
        >
          ← Back to Good Grief
        </Link>
      </Shell>
    );
  }

  if (state === "pending") {
    return (
      <Shell>
        <h1 className="font-display text-5xl leading-[0.9]" data-testid="payment-pending-heading">
          ALMOST THERE
        </h1>
        <p className="font-mono-ui text-sm mt-6 opacity-80">
          Your payment is still processing. If you completed checkout, you’ll get a confirmation email shortly.
        </p>
        <Link
          to="/"
          className="inline-block mt-10 font-mono-ui text-xs uppercase tracking-widest border-2 border-[#F2EFEB] px-6 py-3 hover:bg-[#DFFF00] hover:text-[#090909] hover:border-[#DFFF00] transition-colors"
        >
          ← Back to Good Grief
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="font-display text-5xl leading-[0.9] text-[#FF2A00]" data-testid="payment-error-heading">
        SOMETHING SLIPPED
      </h1>
      <p className="font-mono-ui text-sm mt-6 opacity-80">
        We couldn’t confirm this order. If you were charged, email us and we’ll sort it out.
      </p>
      <Link
        to="/"
        className="inline-block mt-10 font-mono-ui text-xs uppercase tracking-widest border-2 border-[#F2EFEB] px-6 py-3 hover:bg-[#DFFF00] hover:text-[#090909] hover:border-[#DFFF00] transition-colors"
      >
        ← Back to Good Grief
      </Link>
    </Shell>
  );
}

export function PaymentCancel() {
  useLenis();
  return (
    <Shell>
      <span className="overline text-[#FF2A00]">Checkout cancelled</span>
      <h1 className="font-display text-6xl md:text-7xl leading-[0.85] mt-4" data-testid="payment-cancel-heading">
        NO<br />GRIEF.
      </h1>
      <p className="font-serif-ed italic text-lg mt-6 opacity-80">
        Your bag is still stacked. Pick up where you left off whenever you’re ready.
      </p>
      <Link
        to="/"
        data-testid="payment-cancel-home-btn"
        className="inline-block mt-10 font-mono-ui text-xs uppercase tracking-widest border-2 border-[#F2EFEB] px-6 py-3 hover:bg-[#DFFF00] hover:text-[#090909] hover:border-[#DFFF00] transition-colors"
      >
        ← Back to shopping
      </Link>
    </Shell>
  );
}
